// src/components/Channels/ChannelFormModal.js
import React, { useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { XMarkIcon, PhotoIcon } from "@heroicons/react/24/outline";
import {
  createChannel,
  updateChannel,
  fetchChannels,
} from "../../redux/slices/channelsSlice";
import { fetchCategories } from "../../redux/slices/categoriesSlice";
import { showNotification } from "../../redux/slices/notificationSlice";
import { useTranslation } from "../../contexts/LanguageContext";

const BIDDER_TYPES = ["personal", "companies", "any", "verified"];
const PAYMENT_OPTIONS = ["paynow", "paylater", "all"];
const STATUSES = [
  "scheduled", "active", "live", "noshow", "completed",
  "draft", "cancelled", "blocked", "deleted",
];

// Convert an ISO date string to the value HTML <input type="datetime-local"> expects.
const isoToDatetimeLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const emptyForm = {
  userId: "",
  categoryId: "",
  title: "",
  description: "",
  startTime: "",
  endTime: "",
  duration: "",
  bidderType: "any",
  entryAmount: "0",
  cartClearanceTime: "",
  checkOutClearanceTime: "",
  paymentOptions: "all",
  status: "scheduled",
  remarks: "",
  products: "",
};

function ChannelFormModal({ isOpen, setIsOpen, channel, currentPage = 1, pageSize = 10 }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const { categories = [] } = useSelector((s) => s.categories || {});
  const { saving } = useSelector((s) => s.channels || {});

  const isEdit = Boolean(channel?._id);

  const [formData, setFormData] = useState(emptyForm);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState("");
  const [errors, setErrors] = useState({});

  // Load categories once when the modal first opens.
  useEffect(() => {
    if (isOpen && categories.length === 0) {
      dispatch(fetchCategories());
    }
  }, [isOpen, dispatch, categories.length]);

  // Reset/prefill the form whenever the modal opens or the target channel changes.
  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setThumbnailFile(null);
    if (channel) {
      setFormData({
        userId: channel.userId?._id || channel.userId || "",
        categoryId: channel.categoryId?._id || channel.categoryId || "",
        title: channel.title || "",
        description: channel.description || "",
        startTime: isoToDatetimeLocal(channel.startTime),
        endTime: isoToDatetimeLocal(channel.endTime),
        duration: channel.duration != null ? String(channel.duration) : "",
        bidderType: channel.bidderType || "any",
        entryAmount: channel.entryAmount != null ? String(channel.entryAmount) : "0",
        cartClearanceTime: channel.cartClearanceTime != null ? String(channel.cartClearanceTime) : "",
        checkOutClearanceTime: channel.checkOutClearanceTime != null ? String(channel.checkOutClearanceTime) : "",
        paymentOptions: channel.paymentOptions || "all",
        status: channel.status || "scheduled",
        remarks: channel.remarks || "",
        products: channel.products ? JSON.stringify(channel.products, null, 2) : "",
      });
      setThumbnailPreview(channel.thumbnail || "");
    } else {
      setFormData(emptyForm);
      setThumbnailPreview("");
    }
  }, [isOpen, channel]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setThumbnailFile(file);
    const reader = new FileReader();
    reader.onload = () => setThumbnailPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const next = {};
    if (!formData.categoryId) next.categoryId = true;
    if (!formData.title.trim()) next.title = true;
    if (!formData.startTime) next.startTime = true;
    if (!formData.duration) next.duration = true;
    if (formData.products) {
      try {
        const parsed = JSON.parse(formData.products);
        if (!Array.isArray(parsed)) next.products = t("channelForm.invalidProductsJson");
      } catch {
        next.products = t("channelForm.invalidProductsJson");
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const fd = new FormData();
    // Required by both create and update; backend accepts the empty string to no-op.
    fd.append("categoryId", formData.categoryId);
    fd.append("title", formData.title);
    if (formData.description) fd.append("description", formData.description);
    if (formData.userId) fd.append("userId", formData.userId);
    fd.append("startTime", new Date(formData.startTime).toISOString());
    if (formData.endTime) fd.append("endTime", new Date(formData.endTime).toISOString());
    fd.append("duration", String(Number(formData.duration) || 0));
    fd.append("bidderType", formData.bidderType);
    if (formData.entryAmount !== "") fd.append("entryAmount", String(Number(formData.entryAmount) || 0));
    if (formData.cartClearanceTime !== "") fd.append("cartClearanceTime", String(Number(formData.cartClearanceTime) || 0));
    if (formData.checkOutClearanceTime !== "") fd.append("checkOutClearanceTime", String(Number(formData.checkOutClearanceTime) || 0));
    fd.append("paymentOptions", formData.paymentOptions);
    fd.append("status", formData.status);
    if (formData.remarks) fd.append("remarks", formData.remarks);
    if (formData.products) fd.append("products", formData.products); // already JSON-stringified text
    if (thumbnailFile) fd.append("thumbnail", thumbnailFile);

    try {
      if (isEdit) {
        await dispatch(updateChannel({ id: channel._id, formData: fd })).unwrap();
        dispatch(showNotification({ message: t("channelForm.updatedSuccess"), type: "success" }));
      } else {
        await dispatch(createChannel(fd)).unwrap();
        dispatch(showNotification({ message: t("channelForm.createdSuccess"), type: "success" }));
      }
      dispatch(fetchChannels({ currentPage, limit: pageSize }));
      setIsOpen(false);
    } catch (err) {
      dispatch(showNotification({
        message: err || t(isEdit ? "channelForm.failedUpdate" : "channelForm.failedCreate"),
        type: "error",
      }));
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setIsOpen(false)}>
        <Transition.Child as={Fragment}
          enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-40" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child as={Fragment}
            enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
            leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
            <Dialog.Panel className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white rounded-lg shadow-xl">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                <Dialog.Title className="text-lg font-semibold text-dashboardDark">
                  {isEdit ? t("channelForm.editTitle") : t("channelForm.addTitle")}
                </Dialog.Title>
                <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-gray-100 rounded-full">
                  <XMarkIcon className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("channelForm.titleLabel")} <span className="text-red-500">*</span>
                  </label>
                  <input type="text" name="title" value={formData.title} onChange={handleChange}
                    placeholder={t("channelForm.titlePlaceholder")}
                    className={`w-full px-3 py-2 border rounded-md ${errors.title ? "border-red-500" : "border-gray-300"}`} />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("channelForm.categoryId")} <span className="text-red-500">*</span>
                  </label>
                  <select name="categoryId" value={formData.categoryId} onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md ${errors.categoryId ? "border-red-500" : "border-gray-300"}`}>
                    <option value="">{t("channelForm.selectCategory")}</option>
                    {categories.map((c) => (
                      <option key={c._id} value={c._id}>{c.categoryName}</option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("channelForm.description")}</label>
                  <textarea name="description" rows={2} value={formData.description} onChange={handleChange}
                    placeholder={t("channelForm.descriptionPlaceholder")}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                </div>

                {/* Thumbnail */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("channelForm.thumbnail")}</label>
                  <div onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-md p-4 cursor-pointer hover:border-dashboardCoral text-center">
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                    {thumbnailPreview ? (
                      <img src={thumbnailPreview} alt="" className="mx-auto h-24 object-contain rounded" />
                    ) : (
                      <>
                        <PhotoIcon className="w-10 h-10 mx-auto text-gray-400" />
                        <p className="text-sm text-gray-500">{t("channelForm.clickToUpload")}</p>
                      </>
                    )}
                    <p className="text-xs text-gray-400 mt-1">{t("channelForm.thumbnailHelp")}</p>
                    {thumbnailPreview && (
                      <p className="text-xs text-dashboardCoral mt-1">{t("channelForm.clickToChange")}</p>
                    )}
                  </div>
                </div>

                {/* Start / End time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("channelForm.startTime")} <span className="text-red-500">*</span>
                  </label>
                  <input type="datetime-local" name="startTime" value={formData.startTime} onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md ${errors.startTime ? "border-red-500" : "border-gray-300"}`} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("channelForm.endTime")}</label>
                  <input type="datetime-local" name="endTime" value={formData.endTime} onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                </div>

                {/* Duration / Entry amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("channelForm.duration")} <span className="text-red-500">*</span>
                  </label>
                  <input type="number" min="0" name="duration" value={formData.duration} onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md ${errors.duration ? "border-red-500" : "border-gray-300"}`} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("channelForm.entryAmount")}</label>
                  <input type="number" min="0" step="0.01" name="entryAmount" value={formData.entryAmount} onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                </div>

                {/* Cart / Checkout clearance */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("channelForm.cartClearanceTime")}</label>
                  <input type="number" min="0" name="cartClearanceTime" value={formData.cartClearanceTime} onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("channelForm.checkOutClearanceTime")}</label>
                  <input type="number" min="0" name="checkOutClearanceTime" value={formData.checkOutClearanceTime} onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                </div>

                {/* Bidder type / Payment options */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("channelForm.bidderType")}</label>
                  <select name="bidderType" value={formData.bidderType} onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md">
                    {BIDDER_TYPES.map((b) => (
                      <option key={b} value={b}>{t(`channels.bidderType.${b}`)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("channelForm.paymentOptions")}</label>
                  <select name="paymentOptions" value={formData.paymentOptions} onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md">
                    {PAYMENT_OPTIONS.map((p) => (
                      <option key={p} value={p}>{t(`channels.paymentOptions.${p}`)}</option>
                    ))}
                  </select>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("channelForm.status")}</label>
                  <select name="status" value={formData.status} onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md">
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    ))}
                  </select>
                </div>

                {/* User ID (only useful on create) */}
                {!isEdit && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("channelForm.userId")}</label>
                    <input type="text" name="userId" value={formData.userId} onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                    <p className="mt-1 text-xs text-gray-500">{t("channelForm.userIdHelp")}</p>
                  </div>
                )}

                {/* Remarks */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("channelForm.remarks")}</label>
                  <textarea name="remarks" rows={2} value={formData.remarks} onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                </div>

                {/* Products JSON */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("channelForm.products")}</label>
                  <textarea name="products" rows={4} value={formData.products} onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md font-mono text-xs ${errors.products ? "border-red-500" : "border-gray-300"}`} />
                  {errors.products
                    ? <p className="mt-1 text-xs text-red-500">{errors.products}</p>
                    : <p className="mt-1 text-xs text-gray-500">{t("channelForm.productsHelp")}</p>}
                </div>

                {/* Actions */}
                <div className="md:col-span-2 flex justify-end gap-3 pt-2 border-t border-gray-200">
                  <button type="button" onClick={() => setIsOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                    {t("channelForm.cancel")}
                  </button>
                  <button type="submit" disabled={saving}
                    className="px-4 py-2 bg-dashboardCoral text-white rounded-md hover:bg-opacity-90 disabled:opacity-50">
                    {saving ? t("channelForm.saving") : (isEdit ? t("channelForm.save") : t("channelForm.create"))}
                  </button>
                </div>
              </form>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

export default ChannelFormModal;
